/**
 * The password rule, as the console shows it: a live checklist under the
 * password field while it is typed.
 *
 * A mirror of apps/api/src/shared/validation/password.ts — the server is the
 * authority and refuses anything this passes that it does not;
 * test/password-policy.spec.ts keeps the two in agreement.
 */
export const PASSWORD_MIN_LENGTH = 8;

export const PASSWORD_RULES: ReadonlyArray<{ key: string; label: string; test: (p: string) => boolean }> = [
  { key: 'length', label: `At least ${PASSWORD_MIN_LENGTH} characters`, test: (p) => p.length >= PASSWORD_MIN_LENGTH },
  { key: 'upper', label: 'An upper-case letter (A–Z)', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'A lower-case letter (a–z)', test: (p) => /[a-z]/.test(p) },
  { key: 'number', label: 'A number (0–9)', test: (p) => /[0-9]/.test(p) },
  { key: 'special', label: 'A special character, such as @ # $ % !', test: (p) => /[^A-Za-z0-9\s]/.test(p) },
  { key: 'common', label: 'Not an easily guessed password', test: (p) => !isGuessable(p) },
];

/** Words and runs that guessing lists try first, matched after the decorations are stripped. */
const GUESSABLE = [
  'password', 'passw0rd', 'qwerty', 'asdfgh', 'zxcvbn', 'letmein', 'welcome', 'admin',
  'iloveyou', 'monkey', 'dragon', 'abc123', 'drultimate', 'pravinex', 'india',
];

function isGuessable(p: string): boolean {
  const core = p.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!core) return false;
  // One character repeated, or a straight run like 12345678 / abcdefgh.
  if (/^(.)\1+$/.test(core)) return true;
  const ascending = '0123456789abcdefghijklmnopqrstuvwxyz';
  const descending = [...ascending].reverse().join('');
  if (core.length >= 6 && (ascending.includes(core) || descending.includes(core))) return true;
  // A guessing-list word with only digits and symbols around it: Password@123, Welcome#2026.
  const letters = core.replace(/[0-9]/g, '');
  return GUESSABLE.some((w) => letters === w.replace(/[0-9]/g, '') || core === w);
}

/** The rules a password breaks, as sentences for the person typing it. Empty means it passes. */
export function passwordProblems(p: string): string[] {
  return PASSWORD_RULES.filter((r) => !r.test(p)).map((r) => r.label);
}
